/* ============================================================
   köbi · Judge.me Cards-Carousel — Polish & Dots
   ------------------------------------------------------------
   1. `.jdgm-star`-Spans bekommen ihren Stern-Text als textContent
      injiziert (★ für jdgm--on, ☆ für jdgm--off). Das umgeht
      die Tofu-Probleme der JM-Icon-Font komplett — CSS-only
      Lösungen sind unzuverlässig, weil JM verschiedene
      Pseudo-Strategien je nach Widget-Version nutzt.
   2. Dots-Pagination wird zwischen die beiden JM-Pfeile
      injiziert (analog `.slider-counter--dots` aus dem
      Theme-Slider-Pattern), Click/Active-Sync an JM-API.
   ============================================================ */
(function () {
  'use strict';

  function paintStar(star) {
    star.textContent = star.classList.contains('jdgm--off') ? '☆' : '★';
    // JM setzt inline `style="color: rgb(0,0,0)"` — das ist OK weil unser
    // Skin schwarze Sterne will. Falls JM die Klasse umflippt (on ↔ off)
    // setzen wir Textinhalt immer wieder neu via MutationObserver unten.
  }

  function paintAllStars(root) {
    (root || document).querySelectorAll('.jdgm-cards-carousel .jdgm-star').forEach(paintStar);
  }

  /* === Layout-Inline-Styles (1:1 nach Figma node 13:191) ===
     JM-CSS lädt late + nutzt teils !important. Wir setzen alle Properties
     per `setProperty(name, value, 'important')` — Inline + !important
     schlägt jeden Stylesheet-Eintrag, auch JM mit !important.            */
  var FONT_FAMILY = '"Bricolage Grotesque", system-ui, -apple-system, sans-serif';

  function setStyles(el, styles) {
    if (!el) return;
    Object.keys(styles).forEach(function (prop) {
      el.style.setProperty(prop, styles[prop], 'important');
    });
  }

  function styleCard(card) {
    // idempotenz-flag: wir stylen einmal pro Card, aber MutationObserver
    // ruft uns für neue Clones nochmal auf.
    if (card.dataset.koebiCardStyled === '1') return;
    card.dataset.koebiCardStyled = '1';

    // --clamp aus dem Section-Setting (Review length) ablesen — JM setzt
    // den Wert per Inline-Style auf `.jdgm-cards-carousel`. Wir lesen
    // computed, damit Inheritance + Defaults berücksichtigt sind.
    var cardWrapper = card.closest('.jdgm-cards-carousel');
    var clampVal = 6;
    if (cardWrapper) {
      var raw = getComputedStyle(cardWrapper).getPropertyValue('--clamp');
      var parsed = parseInt(raw, 10);
      if (!isNaN(parsed) && parsed > 0) clampVal = parsed;
    }

    // ── Card-Container ─────────────────────────────────────────
    setStyles(card, {
      'display': 'flex',
      'flex-direction': 'column',
      'align-items': 'stretch',
      'text-align': 'left',
      'gap': '10px',                                /* Figma: space/10 */
      'background': '#f5f3f0',                      /* bg/card */
      'color': '#1f1f1f',                           /* text/primary */
      'border': '1px solid #d6d6d6',                /* border/default */
      'border-radius': '12px',                      /* radius/lg */
      'padding': '16px',                            /* Figma: space/16 */
      'box-sizing': 'border-box',
      'height': '100%',
      'box-shadow': 'none',
      'overflow': 'hidden',
      'font-family': FONT_FAMILY,
      'font-size': '16px',
      'line-height': '24px',
      'font-weight': '400',
      'font-style': 'normal'
    });

    // ── Rating-Section auflösen (Stars + Reviewer + Produkt
    //    werden direkte Flex-Kinder der Card) ─────────────────
    var ratingSection = card.querySelector('.jdgm-rating-section');
    if (ratingSection) {
      setStyles(ratingSection, { 'display': 'contents' });
    }

    // ── Stars-Container ────────────────────────────────────────
    var stars = card.querySelector('.jdgm-stars');
    if (stars) {
      setStyles(stars, {
        'display': 'flex',
        'align-items': 'center',
        'justify-content': 'flex-start',
        'gap': '2px',
        'line-height': '1',
        'width': '100%',
        'margin': '0',
        'padding': '0',
        'order': '1',
        'font-size': '16px'
      });
    }

    // ── Einzelne Sterne (Style + textContent in einem Pass) ────
    card.querySelectorAll('.jdgm-star').forEach(function (s) {
      // textContent direkt hier setzen — robuster als separater paintAllStars-Pass
      s.textContent = s.classList.contains('jdgm--off') ? '☆' : '★';
      setStyles(s, {
        'display': 'inline-flex',
        'align-items': 'center',
        'justify-content': 'center',
        'width': 'auto',
        'height': 'auto',
        'min-width': '0',
        'min-height': '0',
        'font-family': FONT_FAMILY,
        'font-size': '16px',
        'line-height': '1',
        'font-weight': '400',
        'font-style': 'normal',
        'color': '#1f1f1f',
        'margin': '0',
        'padding': '0'
      });
    });

    // ── Quote-Text ────────────────────────────────────────────
    // `display: -webkit-box` + `-webkit-line-clamp: var(--clamp)` ist JM's
    // Mechanismus für die Review-Length-Truncation (mit „…"). Die
    // `--clamp`-Variable kommt vom Section-Setting im Theme-Editor.
    var text = card.querySelector('.jdgm-text');
    if (text) {
      // Outer-Container: KEIN display: -webkit-box hier — sonst werden
      // mehrere <p>-Children als getrennte Block-Items mit Zeilen-Logik
      // gezählt, und der Clamp-Mechanismus wird unzuverlässig. Wir klammern
      // stattdessen direkt am (einzigen, zusammengeführten) <p> (siehe unten).
      setStyles(text, {
        'display': 'block',
        'width': '100%',
        'max-width': 'none',
        'min-width': '0',
        'margin': '0',
        'margin-left': '0',
        'margin-right': '0',
        'padding': '0',
        'padding-left': '0',
        'padding-right': '0',
        'padding-inline': '0',
        'padding-inline-start': '0',
        'padding-inline-end': '0',
        'order': '2',
        'font-family': FONT_FAMILY,
        'font-size': '16px',                       /* Body/M */
        'line-height': '24px',
        'font-weight': '400',
        'color': '#1f1f1f',
        'text-align': 'left',
        'box-sizing': 'border-box'
      });

      // Bug-Fix 2026-05-25: Bewertungen mit mehreren Absätzen wurden bisher
      // pro <p> einzeln auf clampVal Zeilen gekürzt — der Clamp muss aber
      // den GESAMTEN Reviewtext umspannen. Lösung: vor dem Clampen alle
      // <p>-Children in einen einzigen <p> mit <br><br>-Separatoren mergen.
      // Idempotent über `koebiTextMerged`, damit Card-Clones (Carousel-Loop)
      // den Text nicht doppelt zusammenpacken.
      if (text.dataset.koebiTextMerged !== '1') {
        var paragraphs = text.querySelectorAll('p');
        if (paragraphs.length > 1) {
          var combinedHtml = Array.prototype.map.call(paragraphs, function (p) {
            return p.innerHTML;
          }).join('<br><br>');
          var firstP = paragraphs[0];
          for (var i = paragraphs.length - 1; i >= 1; i--) {
            paragraphs[i].remove();
          }
          firstP.innerHTML = combinedHtml;
        }
        text.dataset.koebiTextMerged = '1';
      }

      // Line-Clamp auf das <p> selbst — das ist der robuste Weg.
      // `-webkit-box` + `-webkit-line-clamp` + `-webkit-box-orient: vertical`
      // + `overflow: hidden` MÜSSEN auf demselben Element sein, und das
      // Element darf nur inline-Content (Text + <br>) enthalten.
      text.querySelectorAll('p').forEach(function (p) {
        setStyles(p, {
          'display': '-webkit-box',
          '-webkit-box-orient': 'vertical',
          '-webkit-line-clamp': String(clampVal),
          'line-clamp': String(clampVal),
          'overflow': 'hidden',
          'width': '100%',
          'max-width': 'none',
          'min-width': '0',
          'margin': '0',
          'margin-left': '0',
          'margin-right': '0',
          'padding': '0',
          'padding-left': '0',
          'padding-right': '0',
          'padding-inline': '0',
          'text-indent': '0',
          'font-family': FONT_FAMILY,
          'font-size': '16px',
          'line-height': '24px',
          'font-weight': '400',
          'color': '#1f1f1f',
          'text-align': 'left',
          'box-sizing': 'border-box'
        });
      });
    }

    // ── Reviewer-Info (Name + Verified-Icon) ──────────────────
    var reviewer = card.querySelector('.jdgm-reviewer-info');
    if (reviewer) {
      setStyles(reviewer, {
        'display': 'flex',
        'align-items': 'center',
        'justify-content': 'flex-start',
        'gap': '8px',                              /* Figma: space/8 */
        'width': '100%',
        'margin': '0',
        'margin-top': '-6px',                      /* card-gap 10 → effektiv 4 zu Quote */
        'padding': '0',
        'order': '3'
      });
    }

    // ── Reviewer-Name (H6 Bold) ───────────────────────────────
    var name = card.querySelector('.jdgm-reviewer-name');
    if (name) {
      setStyles(name, {
        'display': 'inline',
        'font-family': FONT_FAMILY,
        'font-size': '16px',                       /* size/base */
        'line-height': '24px',                     /* line-height/h6 */
        'font-weight': '700',
        'color': '#1f1f1f',
        'margin': '0',
        'padding': '0'
      });
    }

    // ── Verified-Icon SVG ─────────────────────────────────────
    var verifiedSvg = card.querySelector('.jdgm-reviewer-verified-icon svg');
    if (verifiedSvg) {
      setStyles(verifiedSvg, {
        'width': '16px',
        'height': '16px',
        'color': '#1f1f1f'
      });
    }
    var verifiedWrap = card.querySelector('.jdgm-reviewer-verified-icon');
    if (verifiedWrap) {
      setStyles(verifiedWrap, {
        'display': 'inline-flex',
        'align-items': 'center',
        'margin': '0',
        'padding': '0',
        'color': '#1f1f1f'
      });
    }

    // ── Produkt-Name (Body/S, secondary) ──────────────────────
    var product = card.querySelector('.jdgm-product-name');
    if (product) {
      setStyles(product, {
        'display': 'block',
        'width': '100%',
        'margin': '0',
        'margin-top': '-10px',                     /* card-gap 10 → effektiv 0 zu Reviewer */
        'padding': '0',
        'order': '4',
        'font-family': FONT_FAMILY,
        'font-size': '14px',                       /* size/sm */
        'line-height': '22px',                     /* line-height/body-s */
        'font-weight': '400',
        'color': '#5c5c5c',                        /* text/secondary */
        'text-align': 'left'
      });
      product.querySelectorAll('a').forEach(function (a) {
        setStyles(a, {
          'color': 'inherit',
          'text-decoration': 'none',
          'font-family': FONT_FAMILY,
          'font-size': '14px',
          'line-height': '22px',
          'font-weight': '400'
        });
      });
    }
  }

  function styleAllCards(root) {
    (root || document).querySelectorAll('.jdgm-cards-carousel .jdgm-card').forEach(styleCard);
  }

  function computeVisibleCols(refWidth) {
    // Maßstab ist die tatsächliche Container-Breite, NICHT window.innerWidth —
    // im Theme-Editor-Mobile-Preview ist window.innerWidth = Browser-Window,
    // nicht das iframe.
    var w = refWidth || window.innerWidth;
    if (w < 500) return 1;
    if (w < 750) return 2;
    if (w < 990) return 3;
    return 4;
  }

  function applyCardWidths(wrapper) {
    var content = wrapper.querySelector('.jdgm-content');
    if (!content) return;
    var contentW = content.offsetWidth;
    if (!contentW) return;
    var cols = computeVisibleCols(contentW);
    var gap = 16;
    // JM nutzt `--slides-count` für seine Track-Logik (Translate-Schritt).
    // Auf Mobile soll's 1 sein, nicht der 4-Default vom Desktop.
    wrapper.style.setProperty('--slides-count', String(cols), 'important');
    // Multi-Col (Desktop): 2 px Reserve gegen Sub-Pixel-Rundung der
    // letzten Card. Single-Col (Mobile): KEINE Reserve, damit die
    // nächste Card exakt außerhalb des Cutoffs (= viewport-edge) startet
    // und keine 1-2 px sichtbar bleiben.
    var reserve = cols > 1 ? 2 : 0;
    var cardW = Math.floor((contentW - (cols - 1) * gap) / cols) - reserve;
    if (cardW < 1) return;
    wrapper.querySelectorAll('.jdgm-card').forEach(function (card) {
      card.style.setProperty('width', cardW + 'px', 'important');
      card.style.setProperty('min-width', cardW + 'px', 'important');
      card.style.setProperty('max-width', cardW + 'px', 'important');
      card.style.setProperty('flex', '0 0 ' + cardW + 'px', 'important');
      card.style.setProperty('flex-basis', cardW + 'px', 'important');
      card.style.setProperty('flex-grow', '0', 'important');
      card.style.setProperty('flex-shrink', '0', 'important');
      card.style.setProperty('margin', '0', 'important');
      card.style.setProperty('margin-left', '0', 'important');
      card.style.setProperty('margin-right', '0', 'important');
    });

    // Track-Container: explizit Flex + gap 16, damit JM keine eigenen
    // Card-Margins anwendet, die zur Card-Width dazukommen.
    var track = wrapper.querySelector('.jdgm-videos-container');
    if (track) {
      setStyles(track, {
        'display': 'flex',
        'gap': gap + 'px',
        'margin': '0',
        'padding': '0'
      });
    }
  }

  // Initial-Position links-buendig korrigieren.
  // JM setzt beim Mount einen translateX, der die Clone-Vor-Karten
  // ueberspringt — berechnet mit JMs EIGENER Card-Breite/Gap. Nach
  // applyCardWidths stimmt diese Geometrie nicht mehr, sodass der Offset
  // minimal zu klein ist und links ein Streifen der Clone-Karte sichtbar
  // bleibt („Anschnitt links" beim Initial-Render; verschwindet erst nach
  // dem ersten Autoslide, weil JM dann mit den gerenderten Breiten neu
  // rechnet). Wir setzen den Track einmalig auf die exakte linksbuendige
  // Startposition (Step = unsere Card-Breite + Gap), instant und bevor der
  // Carousel via data-koebi-ready sichtbar wird.
  function alignInitialPosition(wrapper) {
    var track = wrapper.querySelector('.jdgm-videos-container');
    if (!track) return;

    var firstReal = track.querySelector(
      '.jdgm-card:not(.jdgm-clone-before):not(.jdgm-clone-after)'
    );
    if (!firstReal) return;
    var cardWidth = firstReal.offsetWidth;
    if (!cardWidth) return;

    var step = cardWidth + 16; // gap zwischen Cards (siehe applyCardWidths)
    var cloneBeforeCount = track.querySelectorAll(
      '.jdgm-card.jdgm-clone-before'
    ).length;
    var targetX = -(cloneBeforeCount * step);

    // Instant setzen: Transition kurz deaktivieren, damit kein sichtbarer
    // Sprung entsteht. Reflow erzwingen, bevor wir die Transition wieder
    // aktivieren, sonst animiert der Browser den Korrektur-Shift.
    var prevTransition = track.style.transition;
    track.style.transition = 'none';
    track.style.transform = 'translate3d(' + targetX + 'px, 0, 0)';
    void track.offsetWidth;
    track.style.transition = prevTransition;
  }

  function styleSection(wrapper) {
    // 1400 px max-width, zentriert, 50 px Padding L/R (Mobile 16 px).
    // .page-width drumherum ist breiter (--page-width = 140rem ≈ 2240 px)
    // — zentriert uns automatisch.
    var isMobile = window.matchMedia('(max-width: 749px)').matches;

    if (isMobile) {
      // Mobile: aus dem `.page-width`-Padding (16 px L/R) ausbrechen mit
      // negativem Margin, dann beidseitig 16 px eigenes Padding. Cards
      // bekommen so links UND rechts 16 px Abstand zum Bildschirmrand.
      // `overflow: hidden` auf der äußersten Carousel-Ebene verhindert,
      // dass der breite Track die Page seitlich aufzieht.
      setStyles(wrapper, {
        'display': 'block',
        'max-width': 'none',
        'width': 'auto',
        'margin-top': '40px',
        'margin-bottom': '40px',
        'margin-left': '-16px',
        'margin-right': '-16px',
        'padding-left': '16px',
        'padding-right': '16px',
        'padding-top': '0',
        'padding-bottom': '0',
        'box-sizing': 'border-box',
        'overflow': 'hidden'
      });
    } else {
      setStyles(wrapper, {
        'display': 'block',
        'max-width': '1400px',
        'width': '100%',
        'margin-top': '60px',
        'margin-bottom': '60px',
        'margin-left': 'auto',
        'margin-right': 'auto',
        'padding-left': '0',
        'padding-right': '0',
        'padding-top': '0',
        'padding-bottom': '0',
        'box-sizing': 'border-box',
        'overflow': 'visible'
      });
    }

    // Inner-Container: padding/margin neutralisieren. `.jdgm-cards-wrapper`
    // bekommt absichtlich vertikales Padding (8 px), damit der Hover-
    // translateY der Cards nicht von einem darüber liegenden
    // `overflow: hidden` (das wir wegen Slider brauchen) abgeschnitten wird.
    //
    // Mobile: overflow muss auf `visible` damit Cards bis zum Bildschirmrand
    // gehen können (das `padding: 0 16px` der Section darf nicht
    // Card-Inhalt abschneiden). JM-Track macht sein eigenes overflow.
    ['.jdgm-content', '.jdgm-videos-container'].forEach(function (sel) {
      var el = wrapper.querySelector(sel);
      if (el) {
        setStyles(el, {
          'padding': '0',
          'padding-left': '0',
          'padding-right': '0',
          'padding-top': '0',
          'padding-bottom': '0',
          'padding-inline': '0',
          'padding-inline-start': '0',
          'padding-inline-end': '0',
          'margin': '0',
          'margin-left': '0',
          'margin-right': '0',
          'width': '100%',
          'max-width': '100%',
          'box-sizing': 'border-box',
          'overflow': isMobile ? 'visible' : ''
        });
      }
    });

    var cardsWrapper = wrapper.querySelector('.jdgm-cards-wrapper');
    if (cardsWrapper) {
      setStyles(cardsWrapper, {
        'padding-top': '8px',
        'padding-bottom': '8px',
        'padding-left': '0',
        'padding-right': '0',
        'margin': '0',
        'width': '100%',
        'max-width': '100%',
        'box-sizing': 'border-box',
        'overflow': isMobile ? 'visible' : 'hidden'
      });
    }

    // Card-Breite forcieren (JM würde sonst `--width-xs: 230px` nutzen)
    applyCardWidths(wrapper);
  }

  function watchStarClasses(wrapper) {
    if (wrapper.dataset.koebiStarsWatched === '1') return;
    wrapper.dataset.koebiStarsWatched = '1';

    var track = wrapper.querySelector('.jdgm-videos-container') || wrapper;

    new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        // Class-Wechsel auf einem Stern (on ↔ off) → neu painten
        if (
          m.type === 'attributes' &&
          m.target &&
          m.target.classList &&
          m.target.classList.contains('jdgm-star')
        ) {
          paintStar(m.target);
          return;
        }
        // Neue Karten/Stars im DOM (z.B. Clones) → einsammeln und stylen
        if (m.addedNodes && m.addedNodes.length) {
          m.addedNodes.forEach(function (node) {
            if (node.nodeType !== 1) return;
            if (node.classList && node.classList.contains('jdgm-star')) {
              paintStar(node);
            }
            if (node.classList && node.classList.contains('jdgm-card')) {
              styleCard(node);
            }
            if (node.querySelectorAll) {
              node.querySelectorAll('.jdgm-star').forEach(paintStar);
              node.querySelectorAll('.jdgm-card').forEach(styleCard);
            }
          });
        }
      });
    }).observe(track, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class']
    });
  }

  function getCSSVarNumber(el, name, fallback) {
    var cs = getComputedStyle(el);
    var v = parseInt(cs.getPropertyValue(name), 10);
    return isNaN(v) || v < 1 ? fallback : v;
  }

  // Position aus dem `transform` des Tracks rekonstruieren. JM navigiert
  // via translateX (translate3d / translateX / matrix). Wir teilen den
  // Shift durch (card-width + gap) → das gibt die aktuelle Slide-Position
  // innerhalb der Originals. Robuster als data-card-index, weil das auf
  // Clones (loop) inflated Werte liefert und den Dot-Indikator dadurch
  // am letzten Slot festkleben lässt.
  function positionFromTransform(track) {
    if (!track) return null;
    // Inline-Style zuerst (das ist was JM aktiv setzt), Computed als
    // Fallback (manche Browser canonicalisieren inline zu matrix).
    var transform = track.style.transform || '';
    if (!transform || transform === 'none') {
      transform = getComputedStyle(track).transform || '';
    }
    if (!transform || transform === 'none') return null;

    var translateX = null;

    var m3 = transform.match(/translate3d\(\s*(-?\d+\.?\d*)px/);
    if (m3) {
      translateX = parseFloat(m3[1]);
    } else {
      var mt = transform.match(/translateX?\(\s*(-?\d+\.?\d*)px/);
      if (mt) {
        translateX = parseFloat(mt[1]);
      } else {
        var mx = transform.match(/matrix(?:3d)?\(([^)]+)\)/);
        if (mx) {
          var values = mx[1].split(',').map(function (v) { return parseFloat(v); });
          // matrix(a, b, c, d, tx, ty) — tx ist Index 4
          // matrix3d(... 16 Werte ...) — tx ist Index 12
          translateX = transform.indexOf('matrix3d') !== -1 ? values[12] : values[4];
        }
      }
    }
    if (translateX === null || isNaN(translateX)) return null;

    var firstReal = track.querySelector(
      '.jdgm-card:not(.jdgm-clone-before):not(.jdgm-clone-after)'
    );
    if (!firstReal) return null;
    var cardWidth = firstReal.offsetWidth;
    if (!cardWidth) return null;
    var step = cardWidth + 16; // gap zwischen Cards (siehe applyCardWidths)

    var cloneBeforeCount = track.querySelectorAll(
      '.jdgm-card.jdgm-clone-before'
    ).length;
    var cloneBeforeOffset = cloneBeforeCount * step;

    return Math.round((-translateX - cloneBeforeOffset) / step);
  }

  function injectDots(wrapper) {
    if (wrapper.dataset.koebiDotsInited === '1') return;

    var arrows = wrapper.querySelector('.jdgm-arrows--bottom');
    if (!arrows) return;
    var arrowButtons = arrows.querySelectorAll('.jdgm-arrow');
    if (arrowButtons.length < 2) return;

    // Nav-Geometrie aus den Theme-Tokens lesen (--koebi-slider-* in
    // layout/theme.liquid). So bleibt der JM-Carousel automatisch in Sync
    // mit allen anderen Theme-Slidern. Fallbacks wie in der CSS-Datei.
    var navCS = getComputedStyle(arrows);
    var navGap = (navCS.getPropertyValue('--koebi-slider-nav-gap') || '').trim() || '16px';
    var navMarginTop = (navCS.getPropertyValue('--koebi-slider-nav-margin-top') || '').trim() || '24px';

    setStyles(arrows, {
      'margin-top': navMarginTop,
      'margin-bottom': '0',
      'display': 'flex',
      'align-items': 'center',
      'justify-content': 'center',
      'gap': navGap
    });

    // Arrow-Buttons + Icons: Theme-Tokens für Größe/Farbe — JM-CSS lädt
    // late und würde sonst 44×44 / 20×20 / eigene Farbe forcen. Opacity
    // bewusst NICHT hier setzen — sonst gewinnt das !important über den
    // CSS-`:disabled`-Selector und der Disabled-State funktioniert nicht.
    var arrowSize = (navCS.getPropertyValue('--koebi-slider-arrow-size') || '').trim() || '24px';
    var arrowIconSize = (navCS.getPropertyValue('--koebi-slider-arrow-icon-size') || '').trim() || '24px';
    var arrowColor = (navCS.getPropertyValue('--koebi-slider-arrow-color') || '').trim() || '#1f1f1f';

    arrowButtons.forEach(function (btn) {
      setStyles(btn, {
        'width': arrowSize,
        'height': arrowSize,
        'min-width': arrowSize,
        'min-height': arrowSize,
        'padding': '0',
        'border': '0',
        'background': 'transparent',
        'color': arrowColor,
        'display': 'inline-flex',
        'align-items': 'center',
        'justify-content': 'center'
      });
      btn.querySelectorAll('svg').forEach(function (svg) {
        setStyles(svg, {
          'width': arrowIconSize,
          'height': arrowIconSize,
          'color': 'inherit'
        });
      });
    });

    var blockId = wrapper.dataset.jdgmBlockId;
    if (!blockId) return;

    var reviewsCount = getCSSVarNumber(wrapper, '--reviews-count', 0);
    if (!reviewsCount) return;

    var dotsContainer = document.createElement('div');
    dotsContainer.className = 'koebi-jdgm-dots';

    // iOS-Photos-Truncation: analog koebi-gallery-dots.js. Bei mehr als
    // 7 Dots wird ein 5er-Fenster zentriert auf den aktiven Dot gehalten,
    // dahinter je 1 small + 1 tiny als Edge-Indikator, der Rest hidden.
    // Sonst wachsen die Dots bei vielen Reviews einfach über den Container
    // hinaus und der aktive Dot wandert aus dem Sichtfenster.
    var TRUNCATE_THRESHOLD = 7;
    var WINDOW_SIZE = 5;

    // Wir tracken die Position selbst statt sie aus JM's data-card-index
    // zu rekonstruieren — JM-Loop nutzt Clones mit teils sequentiellen
    // Indizes >= reviewsCount, was Math.min(idx, total-1) zum Festkleben
    // am letzten Dot führte (Tim 2026-05-25: „Dots ab Slide 7 statisch").
    var currentDotIndex = 0;

    function computeTotal() {
      var visible = getCSSVarNumber(wrapper, '--slides-count', 1);
      return Math.max(1, reviewsCount - visible + 1);
    }

    function setDotStates(activeIdx) {
      var dots = Array.prototype.slice.call(dotsContainer.children);
      var total = dots.length;
      if (!total) return;

      if (total <= TRUNCATE_THRESHOLD) {
        dots.forEach(function (d) { d.removeAttribute('data-dot-state'); });
        return;
      }

      var halfWin = Math.floor(WINDOW_SIZE / 2);
      var winStart = activeIdx - halfWin;
      var winEnd = activeIdx + halfWin;

      // Fenster an die Ränder klemmen — am Anfang/Ende verschiebt sich
      // das Window so, dass es immer 5 normale Dots zeigt.
      if (winStart < 0) {
        winEnd -= winStart;
        winStart = 0;
      }
      if (winEnd > total - 1) {
        winStart -= (winEnd - (total - 1));
        winEnd = total - 1;
        if (winStart < 0) winStart = 0;
      }

      dots.forEach(function (dot, idx) {
        var state;
        if (idx >= winStart && idx <= winEnd) state = 'normal';
        else if (idx === winStart - 1 || idx === winEnd + 1) state = 'small';
        else if (idx === winStart - 2 || idx === winEnd + 2) state = 'tiny';
        else state = 'hidden';
        dot.setAttribute('data-dot-state', state);
      });
    }

    function setActive(idx) {
      var total = dotsContainer.children.length;
      if (!total) return;
      // Clamp statt Modulo-Wrap: die Dots laufen strikt linear von vorne
      // nach hinten. Out-of-range-Reads (z.B. JM-Loop-Clones mit Index
      // >= total waehrend eines Loop-Wraps) werden an die Enden geklemmt
      // statt nach vorne gewrappt — sonst springt der aktive Dot „von
      // hinten nach vorne" (Tim 2026-06-03).
      var clamped = Math.max(0, Math.min(idx, total - 1));
      currentDotIndex = clamped;
      Array.prototype.forEach.call(dotsContainer.children, function (d, i) {
        d.classList.toggle('koebi-jdgm-dot--active', i === clamped);
      });
      setDotStates(clamped);
    }

    function buildDots(total) {
      dotsContainer.innerHTML = '';
      for (var i = 0; i < total; i++) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'koebi-jdgm-dot';
        btn.dataset.dotIndex = i;
        btn.setAttribute('aria-label', 'Slide ' + (i + 1) + ' von ' + total);
        dotsContainer.appendChild(btn);
      }
      setActive(currentDotIndex);
    }

    // Während unsere eigene Multi-Step-Navigation läuft (goToIndex feuert
    // jdgmNextCard alle 80ms), sollen Track-Observer-Reads NICHT die Dot-
    // Anzeige zurücksetzen — sonst springt der Dot zwischen Optimistic-
    // Target und Zwischenständen hin und her (Tim 2026-05-25: „springt vor
    // und zurück"). Während `navigating === true` wird jegliches Track-
    // Sync ignoriert; nach Ablauf der Animation einmal hart resynced.
    var navigating = false;
    var syncDebounce;
    var track = wrapper.querySelector('.jdgm-videos-container');

    function syncFromTrack() {
      if (navigating) return;
      if (!track) return;
      var pos = positionFromTransform(track);
      if (pos !== null) setActive(pos);
    }

    function goToIndex(target) {
      var total = dotsContainer.children.length;
      if (!total) return;
      var clamped = Math.max(0, Math.min(target, total - 1));
      if (clamped === currentDotIndex) return;

      // Linear statt „kürzester Weg": wir schalten genau die Differenz an
      // Karten vor- oder zurueck — NIE ueber die Loop-Grenze gewrappt.
      // Sonst wandert der Slider beim Tippen eines hinteren Dots rueckwaerts
      // durch den Loop („von vorne nach hinten" + uebersprungene Karten,
      // Tim 2026-06-03). Vorwaerts bei positivem Delta, sonst rueckwaerts.
      var delta = clamped - currentDotIndex;
      var fn = delta > 0 ? window.jdgmNextCard : window.jdgmPreviousCard;
      var steps = Math.abs(delta);
      if (typeof fn !== 'function') return;

      // Track-Sync deaktivieren, bevor wir die Step-Schleife starten.
      navigating = true;
      // Optimistisches Dot-Update — JM braucht ~80ms pro Step, der Dot
      // soll sofort reagieren.
      setActive(clamped);

      var i = 0;
      var step = function () {
        if (i >= steps) {
          // Settle: 500ms warten bis JM die letzte Transition abgeschlossen
          // hat, dann Track-Sync wieder freigeben und einmal hart resyncen,
          // um etwaige Drift zu korrigieren (z.B. Loop deaktiviert + Ende
          // erreicht — dann kommt JM nicht so weit wie unser Optimistic).
          setTimeout(function () {
            navigating = false;
            syncFromTrack();
          }, 500);
          return;
        }
        try { fn(blockId); } catch (e) {}
        i++;
        setTimeout(step, 80);
      };
      step();
    }

    arrows.insertBefore(dotsContainer, arrowButtons[1]);
    wrapper.dataset.koebiDotsInited = '1';

    buildDots(computeTotal());

    dotsContainer.addEventListener('click', function (e) {
      var dot = e.target.closest('.koebi-jdgm-dot');
      if (!dot) return;
      var target = parseInt(dot.dataset.dotIndex, 10);
      if (!isNaN(target)) goToIndex(target);
    });

    if (track) {
      // Primärer Sync-Trigger: transitionend feuert exakt einmal pro
      // abgeschlossener Slide-Bewegung (Arrow, Swipe, Autoplay). Keine
      // Mid-Animation-Reads → kein Bounce.
      track.addEventListener('transitionend', function (e) {
        if (e.propertyName === 'transform') syncFromTrack();
      });

      // Fallback für Snaps ohne CSS-Transition (z.B. JM-Loop-Wrap, der
      // den Track instantan zurücksetzt). MutationObserver auf `style`
      // mit 120ms-Debounce — bündelt rapide Style-Updates und liest erst,
      // wenn nichts mehr nachkommt.
      new MutationObserver(function () {
        clearTimeout(syncDebounce);
        syncDebounce = setTimeout(syncFromTrack, 120);
      }).observe(track, {
        attributes: true,
        attributeFilter: ['style']
      });

      // Initial-Sync — falls JM bei mount schon einen translateX-Offset
      // gesetzt hat (z.B. wegen Clone-Vorlauf), übernehmen wir den.
      syncFromTrack();
    }

    // Responsive: bei Resize ändert sich --slides-count → Dot-Anzahl neu.
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        var total = computeTotal();
        if (total !== dotsContainer.children.length) buildDots(total);
      }, 150);
    });
  }

  /* ============================================================
     Header (Title) — Umbrechen statt Ellipsis
     JM setzt teils `white-space: nowrap` + ellipsis. Bei langen
     Titeln (z.B. „4,7 von 5 Sternen — aus über 120 Bewertungen")
     wird der Titel mobil abgeschnitten. Wir erlauben Wrap.
     ============================================================ */

  function styleHeader(wrapper) {
    var isMobile = window.matchMedia('(max-width: 749px)').matches;

    var header = wrapper.querySelector('.jdgm-header');
    if (header) {
      setStyles(header, {
        'display': 'flex',
        'flex-direction': 'column',
        'gap': '4px',
        'text-align': 'center',
        'margin': '0 0 32px',
        'padding': '0',
        'width': '100%',
        'max-width': '100%',
        'position': 'static',                /* falls JM auf absolute steht */
        'white-space': 'normal',
        'overflow': 'visible',
        'text-overflow': 'clip',
        'flex-wrap': 'wrap'
      });
    }

    // Title + ALLE inner spans/p/divs aggressiv auf wrap stellen.
    // JM hat scheinbar einen inneren Wrapper mit `white-space: nowrap`
    // + `text-overflow: ellipsis`, der das `…` produziert.
    var title = wrapper.querySelector('.jdgm-title');
    if (title) {
      var titleStyles = {
        'display': 'block',
        'position': 'static',
        'font-family': FONT_FAMILY,
        'font-weight': '700',
        'font-size': isMobile ? '30px' : '36px',
        'line-height': isMobile ? '36px' : '44px',
        'letter-spacing': '-0.4px',
        'color': '#1f1f1f',
        'margin': '0',
        'padding': '0',
        'width': '100%',
        'max-width': '100%',
        'min-width': '0',
        'white-space': 'normal',
        'overflow': 'visible',
        'text-overflow': 'clip',
        'word-break': 'normal',
        'overflow-wrap': 'break-word',
        '-webkit-line-clamp': 'none',         /* falls JM line-clamp auf Title hat */
        'line-clamp': 'none',
        'text-align': 'center'
      };
      setStyles(title, titleStyles);

      // Rekursiv auf alle Kinder anwenden (spans, p, etc.) — JM nutzt
      // teils einen inneren Wrapper für die Ellipsis.
      title.querySelectorAll('*').forEach(function (child) {
        setStyles(child, {
          'display': 'inline',
          'white-space': 'normal',
          'overflow': 'visible',
          'text-overflow': 'clip',
          'max-width': 'none',
          'width': 'auto',
          '-webkit-line-clamp': 'none',
          'line-clamp': 'none',
          'font-family': FONT_FAMILY,
          'font-size': 'inherit',
          'line-height': 'inherit',
          'font-weight': 'inherit',
          'letter-spacing': 'inherit',
          'color': 'inherit'
        });
      });
    }
  }

  /* ============================================================
     Custom Swipe — Loop-fähig wie die Pfeile
     JM's eingebauter Swipe macht am Ende des Tracks einen
     „Gummiband"-Snap-Back. `jdgmNextCard`/`jdgmPreviousCard`
     dagegen loopen sauber. Wir hängen eigene Touch-Handler an,
     die die gleichen API-Calls auslösen.
     ============================================================ */

  function setupCustomSwipe(wrapper) {
    if (wrapper.dataset.koebiSwipeBound === '1') return;
    wrapper.dataset.koebiSwipeBound = '1';

    var blockId = wrapper.dataset.jdgmBlockId;
    if (!blockId) return;

    // JM's eigene Touch-Logik unterdrücken: `touch-action: pan-y` erlaubt
    // dem Browser nur vertikales Scrollen — horizontaler Drag wird nicht
    // an JM weitergegeben, sondern landet ausschließlich bei unserem
    // Listener. Verhindert das Doppel-Weiterschalten (JM + wir).
    ['.jdgm-cards-wrapper', '.jdgm-videos-container'].forEach(function (sel) {
      var el = wrapper.querySelector(sel);
      if (el) {
        el.style.setProperty('touch-action', 'pan-y', 'important');
      }
    });

    var startX = 0;
    var startY = 0;
    var moved = false;
    var dragging = false;
    var THRESHOLD = 40;       // ab wie vielen px Drag wird's ein Swipe
    var DIRECTION_LOCK = 12;  // ab wie vielen px wird die Richtung gemerkt

    function reset() {
      startX = 0; startY = 0; moved = false; dragging = false;
    }

    function onStart(x, y) {
      startX = x; startY = y; moved = false; dragging = true;
    }

    function onMove(x, y) {
      if (!dragging) return;
      var dx = x - startX;
      var dy = y - startY;
      if (Math.abs(dx) > DIRECTION_LOCK && Math.abs(dx) > Math.abs(dy)) {
        moved = true;
      }
    }

    function onEnd(x) {
      if (!dragging) return;
      var dx = x - startX;
      dragging = false;
      if (!moved) return;
      if (dx <= -THRESHOLD) {
        if (typeof window.jdgmNextCard === 'function') {
          try { window.jdgmNextCard(blockId); } catch (e) {}
        }
      } else if (dx >= THRESHOLD) {
        if (typeof window.jdgmPreviousCard === 'function') {
          try { window.jdgmPreviousCard(blockId); } catch (e) {}
        }
      }
    }

    // Capture-Phase + passive — wir fangen das Event bevor JM's eigener
    // Handler in der Bubble-Phase reagiert. JM macht trotzdem seine
    // Drag-Animation, aber unser jdgmNextCard-Aufruf am Touch-End
    // korrigiert die Position via Loop.
    wrapper.addEventListener('touchstart', function (e) {
      if (!e.touches || !e.touches.length) return;
      onStart(e.touches[0].clientX, e.touches[0].clientY);
    }, { passive: true, capture: true });

    wrapper.addEventListener('touchmove', function (e) {
      if (!e.touches || !e.touches.length) return;
      onMove(e.touches[0].clientX, e.touches[0].clientY);
    }, { passive: true, capture: true });

    wrapper.addEventListener('touchend', function (e) {
      var changed = e.changedTouches && e.changedTouches[0];
      if (!changed) return reset();
      onEnd(changed.clientX);
    }, { passive: true, capture: true });

    wrapper.addEventListener('touchcancel', reset, { passive: true, capture: true });
  }

  /* ============================================================
     Mobile dynamic height (analog koebi-hero-slider.js)
     Wrapper-Höhe = Höhe der aktiven Card. Auf Mobile aktiv.
     ============================================================ */

  function updateCarouselHeight(wrapper, instant) {
    var cardsWrapper = wrapper.querySelector('.jdgm-cards-wrapper');
    if (!cardsWrapper) return;

    var isMobile = window.matchMedia('(max-width: 749px)').matches;
    if (!isMobile) {
      cardsWrapper.style.removeProperty('height');
      cardsWrapper.style.removeProperty('transition');
      return;
    }

    var activeCard = wrapper.querySelector('.jdgm-card.jdgm-carousel-item.active');
    if (!activeCard) {
      activeCard = wrapper.querySelector('.jdgm-card:not(.jdgm-clone-before):not(.jdgm-clone-after)');
    }
    if (!activeCard) return;

    var h = activeCard.offsetHeight || activeCard.scrollHeight;
    if (!h) return;

    // +16 px für das padding-top/-bottom (8 + 8) das wir für die hover-Reserve setzen
    var total = h + 16;

    if (instant) {
      cardsWrapper.style.setProperty('transition', 'none', 'important');
      cardsWrapper.style.setProperty('height', total + 'px', 'important');
      requestAnimationFrame(function () {
        cardsWrapper.style.setProperty('transition', 'height 0.3s ease', 'important');
      });
    } else {
      cardsWrapper.style.setProperty('transition', 'height 0.3s ease', 'important');
      cardsWrapper.style.setProperty('height', total + 'px', 'important');
    }
  }

  function watchCarouselHeight(wrapper) {
    if (wrapper.dataset.koebiHeightWatched === '1') return;
    wrapper.dataset.koebiHeightWatched = '1';

    updateCarouselHeight(wrapper, true);

    var track = wrapper.querySelector('.jdgm-videos-container');
    if (track) {
      // Active-Klasse-Wechsel auf einer Card → Höhe neu rechnen
      new MutationObserver(function () {
        updateCarouselHeight(wrapper);
      }).observe(track, {
        subtree: true,
        attributes: true,
        attributeFilter: ['class']
      });
    }

    // Resize-Listener — Mobile ↔ Desktop-Wechsel + Card-Höhe nach Reflow
    var t;
    window.addEventListener('resize', function () {
      clearTimeout(t);
      t = setTimeout(function () { updateCarouselHeight(wrapper, true); }, 150);
    });
  }

  /* ============================================================
     Modal (Lightbox beim Klick auf eine Review)
     ============================================================ */

  function paintModalStars(starRatingEl) {
    var label = starRatingEl.getAttribute('aria-label') || '';
    var m = label.match(/(\d+(?:[.,]\d+)?)\s*out\s*of\s*(\d+)/i);
    var filled = m ? parseFloat(m[1].replace(',', '.')) : 5;
    var spans = starRatingEl.querySelectorAll('.jm-star-rating__font-icon');
    spans.forEach(function (s, i) {
      s.textContent = i < Math.round(filled) ? '★' : '☆';
      setStyles(s, {
        'font-family': FONT_FAMILY,
        'font-size': '20px',
        'line-height': '1',
        'color': '#1f1f1f',
        'font-style': 'normal'
      });
    });
  }

  function styleModal(modal) {
    if (!modal || modal.dataset.koebiModalStyled === '1') return;
    modal.dataset.koebiModalStyled = '1';

    // Outer Lightbox-Container — Hintergrund, Border, Radius
    var contentOnly = modal.querySelector('.jdgm-modal__container--content-only')
      || (modal.classList.contains('jdgm-modal__container--content-only') ? modal : null);
    if (contentOnly) {
      setStyles(contentOnly, {
        'background': '#f9f8f6',                  /* bg/page */
        'border': '1px solid #c2b6a3',            /* border/warm-200 */
        'border-radius': '12px',
        'padding': '32px',
        'font-family': FONT_FAMILY,
        'color': '#1f1f1f',
        'box-sizing': 'border-box'
      });
    }

    // Close-Button: Lucide-x-feeling — schwarz, Hover dim
    var close = modal.querySelector('.jm-indicator__close');
    if (close) {
      setStyles(close, {
        'background': 'transparent',
        'border': '0',
        'color': '#1f1f1f',
        'padding': '8px',
        'cursor': 'pointer',
        'display': 'inline-flex',
        'align-items': 'center',
        'justify-content': 'center'
      });
      var svg = close.querySelector('svg');
      if (svg) {
        setStyles(svg, { 'width': '14px', 'height': '14px', 'color': '#1f1f1f' });
      }
    }

    // Sterne im Modal
    modal.querySelectorAll('.jm-star-rating').forEach(paintModalStars);

    // Reviewer-Name
    var name = modal.querySelector('.jm-reviewer-info__name');
    if (name) {
      setStyles(name, {
        'font-family': FONT_FAMILY,
        'font-size': '16px',
        'line-height': '24px',
        'font-weight': '700',
        'color': '#1f1f1f'
      });
    }

    // Verified-Badge ("Verifiziert")
    var verifiedWrap = modal.querySelector('.jm-verified-buyer-badge');
    if (verifiedWrap) {
      setStyles(verifiedWrap, {
        'background': '#eeebe8',                  /* neutral/warm-100 */
        'border-radius': '9999px',
        'padding': '2px 8px',
        'display': 'inline-flex',
        'align-items': 'center'
      });
    }
    var verifiedText = modal.querySelector('.jm-verified-buyer-badge__text');
    if (verifiedText) {
      setStyles(verifiedText, {
        'font-family': FONT_FAMILY,
        'font-size': '12px',
        'line-height': '16px',
        'font-weight': '500',
        'color': '#5c5c5c',                       /* text/secondary */
        'margin': '0'
      });
    }

    // Datum + andere muted Text-Spans neben dem Namen
    modal.querySelectorAll('.jm-reviewer-info__details .jm-text').forEach(function (el) {
      // Name + Verified-Text haben eigene Style schon. Date-Span erkennen
      // wir an seiner Position (zweites cluster im jm-stack).
      if (el.classList.contains('jm-reviewer-info__name')) return;
      if (el.classList.contains('jm-verified-buyer-badge__text')) return;
      setStyles(el, {
        'font-family': FONT_FAMILY,
        'font-size': '14px',
        'line-height': '22px',
        'font-weight': '400',
        'color': '#7a7a7a'                        /* text/muted */
      });
    });

    // Quote-Text
    var body = modal.querySelector('.jm-review-content__body');
    if (body) {
      setStyles(body, {
        'font-family': FONT_FAMILY,
        'font-size': '16px',
        'line-height': '24px',
        'font-weight': '400',
        'color': '#1f1f1f'
      });
      body.querySelectorAll('p').forEach(function (p) {
        setStyles(p, {
          'font-family': FONT_FAMILY,
          'font-size': '16px',
          'line-height': '24px',
          'font-weight': '400',
          'color': '#1f1f1f',
          'margin': '0',
          'padding': '0'
        });
      });
    }

    // Produkt-Variant-Card unten (Container mit Produkt-Name)
    var productCard = modal.querySelector('.jm-product-variant-card');
    if (productCard) {
      setStyles(productCard, {
        'background': '#f5f3f0',                  /* bg/card */
        'border': '1px solid #c2b6a3',
        'border-radius': '8px',
        'padding': '16px',
        'box-sizing': 'border-box'
      });
    }
    var productLink = modal.querySelector('.jm-product-variant-card__product-link');
    if (productLink) {
      setStyles(productLink, {
        'font-family': FONT_FAMILY,
        'font-size': '14px',
        'line-height': '22px',
        'font-weight': '400',
        'color': '#5c5c5c',                       /* text/secondary */
        'margin': '0',
        'text-decoration': 'none',
        'cursor': 'pointer'
      });
    }
  }

  function watchForModal() {
    // Modal wird per Vue erst beim Klick gemounted — wir beobachten
    // body auf neue `.jdgm-modal__container`-Nodes.
    new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (!m.addedNodes) return;
        m.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.classList && node.classList.contains('jdgm-modal__container')) {
            styleModal(node);
          }
          if (node.querySelectorAll) {
            node.querySelectorAll('.jdgm-modal__container').forEach(styleModal);
          }
        });
      });
    }).observe(document.body, { childList: true, subtree: true });

    // Falls Modal beim Page-Load schon im DOM ist:
    document.querySelectorAll('.jdgm-modal__container').forEach(styleModal);
  }

  function tryInit() {
    var carousels = document.querySelectorAll('.jdgm-cards-carousel');
    if (!carousels.length) {
      setTimeout(tryInit, 200);
      return;
    }
    var allReady = true;
    carousels.forEach(function (c) {
      if (
        !c.querySelector('.jdgm-arrows--bottom .jdgm-arrow') ||
        !c.querySelector('.jdgm-videos-container .jdgm-card')
      ) {
        allReady = false;
      }
    });
    if (!allReady) {
      setTimeout(tryInit, 200);
      return;
    }
    // Jeder Schritt in try/catch — wenn einer ausfällt, läuft der Rest
    // trotzdem. Ohne das hat ein Crash in styleSection vorher das ganze
    // Carousel verkrüppelt (keine Sterne, keine Dots).
    function safe(fn, label) {
      try { fn(); } catch (e) { console.error('[koebi-jdgm] ' + label + ' failed:', e); }
    }

    carousels.forEach(function (c) {
      safe(function () { styleSection(c); }, 'styleSection');
    });
    carousels.forEach(function (c) {
      safe(function () { styleHeader(c); }, 'styleHeader');
    });
    safe(function () { styleAllCards(); }, 'styleAllCards');
    carousels.forEach(function (c) {
      safe(function () { applyCardWidths(c); }, 'applyCardWidths');
    });
    // Direkt nach den Card-Breiten den Initial-Offset links-buendig fixen,
    // bevor injectDots den Track-Observer haengt und bevor der Carousel
    // sichtbar wird (data-koebi-ready). Behebt den „Anschnitt links".
    carousels.forEach(function (c) {
      safe(function () { alignInitialPosition(c); }, 'alignInitialPosition');
    });
    safe(function () { paintAllStars(); }, 'paintAllStars');
    carousels.forEach(function (c) {
      safe(function () { watchStarClasses(c); }, 'watchStarClasses');
    });
    carousels.forEach(function (c) {
      safe(function () { injectDots(c); }, 'injectDots');
    });
    carousels.forEach(function (c) {
      safe(function () { watchCarouselHeight(c); }, 'watchCarouselHeight');
    });
    // setupCustomSwipe BEWUSST DEAKTIVIERT (Tim 2026-06-04):
    // Unser Touch-Handler rief am touchend zusaetzlich `jdgmNextCard` auf —
    // JM's eigener Swipe schaltet aber bereits eine Karte weiter (das
    // `touch-action: pan-y` unterdrueckt nur Browser-Scroll, NICHT JM's
    // JS-Touch-Logik). Ergebnis mobil: pro Swipe 2 Karten = eine
    // uebersprungen. JM's nativer Swipe schaltet sauber 1 Karte/Step.
    // Funktion bleibt definiert, falls spaeter looping-Swipe gewuenscht
    // ist — dann muss JM's nativer Swipe aber zuverlaessig geblockt werden.
    // carousels.forEach(function (c) {
    //   safe(function () { setupCustomSwipe(c); }, 'setupCustomSwipe');
    // });
    safe(function () { watchForModal(); }, 'watchForModal');

    // Pre-paint Flash unterdrücken: Der Carousel ist bis hierher per
    // `visibility: hidden` versteckt (siehe koebi-judgeme.css). Erst wenn
    // alle Card-Breiten, Wrapper-Overflow + Nav stehen, geben wir frei.
    // Doppelter rAF, damit der Browser die neuen Layout-Werte garantiert
    // berechnet hat, bevor das visibility-Toggle den nächsten Paint löst.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        carousels.forEach(function (c) {
          c.setAttribute('data-koebi-ready', '1');
        });
      });
    });

    // Bei Resize neu setzen (Mobile/Desktop-Switch + Card-Width)
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        carousels.forEach(function (c) {
          safe(function () { styleSection(c); }, 'styleSection-resize');
          safe(function () { styleHeader(c); }, 'styleHeader-resize');
        });
      }, 150);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tryInit);
  } else {
    tryInit();
  }

  // Safety: nach 4s in jedem Fall freigeben, falls JM bis dahin keine
  // Arrows gerendert hat (z.B. zu wenig Reviews → keine Pagination, oder
  // tryInit-Poll hängt). Verhindert, dass der Carousel dauerhaft hidden
  // bleibt. Im Normalfall hat tryInit das Attribut längst vorher gesetzt.
  setTimeout(function () {
    document.querySelectorAll('.jdgm-cards-carousel:not([data-koebi-ready="1"])').forEach(function (c) {
      c.setAttribute('data-koebi-ready', '1');
    });
  }, 4000);
})();

/* ============================================================
   köbi Skin Teil 2: Reviews-Widget (Revamp, Vue-basiert)
   ------------------------------------------------------------
   Container: `.jdgm-widget.jdgm-review-widget.jdgm-widget-revamp`.
   JM exponiert offizielle CSS-Variablen am inneren `.jm-review-widget`
   (siehe HTML-Inline-Style des Containers). Wir setzen diese per JS
   als Inline-Style (mit !important) und ueberschreiben dazu
   - den `max-width: 1200px`-Inline-Style am Container (Quelle der
     visuellen Einrueckung gegenueber der Section-Breite),
   - Buttons (`.jm-button--primary`, `.jm-button--secondary`),
   - Sterne (`.jm-star-rating__font-icon` durch SVG ersetzt — Filled-
     /Outline-Aufteilung aus `aria-label="X out of Y stars"`),
   - Review-Karten (`.jdgm-review-card`).

   Pattern wie beim Cards-Carousel: `setProperty(name, value, 'important')`
   plus MutationObserver fuer Vue-Re-Renders + Hover-Listener (Memory:
   feedback_third_party_plugin_css_override).
   ============================================================ */
(function () {
  'use strict';

  var FONT = '"Bricolage Grotesque", system-ui, -apple-system, sans-serif';
  var TEXT_PRIMARY = '#1f1f1f';
  var TEXT_SECONDARY = '#5c5c5c';
  var BG_CARD = '#f5f3f0';
  var BG_BUTTON_HOVER = '#eeebe8';
  var TEXT_INVERSE = '#f9f8f6';
  var BORDER = '#d6d6d6';

  var FILLED_SVG = '<svg viewBox="0.68 2.75 14.36 14.05" width="100%" height="100%" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M8.64062 3.24219L10.418 6.87891L14.3281 7.45312C14.6562 7.50781 14.9297 7.72656 15.0391 8.05469C15.1484 8.35547 15.0664 8.71094 14.8203 8.92969L11.9766 11.7461L12.6602 15.7383C12.7148 16.0664 12.5781 16.3945 12.3047 16.5859C12.0312 16.8047 11.6758 16.8047 11.375 16.668L7.875 14.7812L4.34766 16.668C4.07422 16.8047 3.71875 16.8047 3.44531 16.5859C3.17188 16.3945 3.03516 16.0664 3.08984 15.7383L3.74609 11.7461L0.902344 8.92969C0.683594 8.71094 0.601562 8.35547 0.683594 8.05469C0.792969 7.72656 1.06641 7.50781 1.39453 7.45312L5.33203 6.87891L7.08203 3.24219C7.21875 2.94141 7.51953 2.75 7.875 2.75C8.20312 2.75 8.50391 2.94141 8.64062 3.24219Z"/></svg>';
  var OUTLINE_SVG = '<svg viewBox="72.6 2.75 14.4 14.05" width="100%" height="100%" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M79.8477 2.75C80.1211 2.75 80.3398 2.91406 80.4492 3.13281L82.3359 6.98828L86.5195 7.61719C86.7656 7.64453 86.957 7.80859 87.0391 8.05469C87.1211 8.30078 87.0664 8.54688 86.875 8.73828L83.8398 11.7461L84.5508 15.9844C84.6055 16.2305 84.4961 16.5039 84.3047 16.6406C84.0859 16.7773 83.8125 16.8047 83.5938 16.6953L79.8477 14.6719L76.1289 16.6953C75.8828 16.8047 75.6367 16.7773 75.418 16.6406C75.2266 16.4766 75.1172 16.2305 75.1719 15.9844L75.8828 11.7461L72.8477 8.73828C72.6562 8.54688 72.6016 8.30078 72.6836 8.05469C72.7656 7.83594 72.957 7.64453 73.2031 7.61719L77.3867 6.98828L79.2734 3.13281C79.3828 2.91406 79.6016 2.75 79.8477 2.75ZM79.8477 4.91016L78.4258 7.89062C78.3164 8.08203 78.1523 8.21875 77.9336 8.24609L74.707 8.71094L77.0312 11.0352C77.1953 11.1992 77.2773 11.418 77.2227 11.6094L76.6758 14.8906L79.5469 13.3594C79.7383 13.25 79.9844 13.25 80.1758 13.3594L83.0469 14.8906L82.5 11.6367C82.4453 11.418 82.5273 11.1992 82.6914 11.0625L85.0156 8.71094L81.7891 8.24609C81.5703 8.21875 81.4062 8.08203 81.2969 7.89062L79.8477 4.91016Z"/></svg>';

  function setStyles(el, styles) {
    if (!el) return;
    Object.keys(styles).forEach(function (prop) {
      el.style.setProperty(prop, styles[prop], 'important');
    });
  }

  /* ---------- Sterne (Revamp: .jm-star-rating mit aria-label) ---------- */

  function styleStars(starRating) {
    /* aria-label parsen: "4 out of 5 stars" → filled=4, scale=5.
       Bug-Workaround: JM-Header zeigt manchmal "X out of 1 stars" —
       scale wird auf 5 normalisiert wenn unplausibel. */
    var aria = starRating.getAttribute('aria-label') || '';
    var m = aria.match(/(\d+(?:[.,]\d+)?)\s*out\s*of\s*(\d+)/i);
    var filled = 0;
    var scale = 5;
    if (m) {
      filled = Math.round(parseFloat(m[1].replace(',', '.')));
      var parsedScale = parseInt(m[2], 10);
      if (parsedScale >= 3 && parsedScale <= 10) scale = parsedScale;
    }
    if (filled > scale) filled = scale;
    if (filled < 0) filled = 0;

    var marker = filled + '/' + scale;
    if (starRating.dataset.koebiStarsRendered === marker) return;
    starRating.dataset.koebiStarsRendered = marker;

    /* JM rendert die Sterne als 1-bis-n Spans `.jm-star-rating__font-icon`
       innerhalb eines `.jm-cluster`. Bei der Header-Variante nur 1 Span
       (Icon-Font-Trick), bei den Review-Karten N Spans. Wir leeren den
       Cluster komplett und bauen N SVG-Spans neu. */
    var cluster = starRating.querySelector('.jm-cluster') || starRating;
    cluster.innerHTML = '';

    setStyles(cluster, {
      'display': 'inline-flex',
      'align-items': 'center',
      'gap': '4px',
      'line-height': '1'
    });

    for (var i = 0; i < scale; i++) {
      var span = document.createElement('span');
      span.className = 'koebi-jm-star';
      span.innerHTML = i < filled ? FILLED_SVG : OUTLINE_SVG;
      setStyles(span, {
        'display': 'inline-flex',
        'align-items': 'center',
        'justify-content': 'center',
        'width': '18px',
        'height': '18px',
        'min-width': '18px',
        'color': TEXT_PRIMARY,
        'font-size': '0',
        'line-height': '1'
      });
      cluster.appendChild(span);
    }
  }

  /* ---------- Container ---------- */

  function styleWidget(widget) {
    /* Inline-Style am Container ueberschreiben — Quelle der Einrueckung
       ist `max-width: 1200px; margin: 0 auto;` direkt auf dem Wrapper. */
    setStyles(widget, {
      'max-width': 'none',
      'width': '100%',
      'margin-left': '0',
      'margin-right': '0',
      'padding-left': '0',
      'padding-right': '0',
      'padding-inline': '0',
      'box-sizing': 'border-box',
      'font-family': FONT,
      'color': TEXT_PRIMARY
    });

    /* JM-CSS-Variablen am inneren .jm-review-widget setzen — offizielle
       Theming-API. Greift fuer alle Sub-Components ohne extra Specifity. */
    var inner = widget.querySelector('.jm-review-widget');
    if (inner) {
      setStyles(inner, {
        '--jm-review-widget-star-and-histogram-color': TEXT_PRIMARY,
        '--jm-review-widget-button-color': TEXT_PRIMARY,
        '--jm-review-widget-button-text-color': TEXT_INVERSE,
        '--jm-review-widget-text-color': TEXT_PRIMARY,
        '--jm-review-widget-lighter-text-color': TEXT_SECONDARY,
        '--jm-widget-primary-color': TEXT_PRIMARY,
        '--jm-widget-border-radius': '16px',
        '--jm-widget-border-radius-simplified': '12px',
        'font-family': FONT,
        'color': TEXT_PRIMARY
      });
    }

    /* Header-Sterne komplett ausblenden — Tim 2026-05-24: im Header
       reicht die Zahl ("4,0  1 Bewertung") ohne Sternchen-Wiederholung.
       Die Sterne in den einzelnen Reviews bleiben (siehe unten). */
    widget.querySelectorAll('.jm-average-rating-display .jm-star-rating').forEach(function (el) {
      setStyles(el, { 'display': 'none' });
    });

    /* Sterne in den Review-Karten ersetzen */
    widget.querySelectorAll('.jdgm-review-card .jm-star-rating').forEach(styleStars);

    /* Text-Komponenten der Header-Summary */
    widget.querySelectorAll('.jm-average-rating-display .jm-text').forEach(function (el, idx) {
      /* Erstes .jm-text = "4.0" (groß), zweites = "1 Bewertung" (Body) */
      if (idx === 0) {
        setStyles(el, {
          'font-family': FONT,
          'font-weight': '700',
          'font-size': '32px',
          'line-height': '1',
          'color': TEXT_PRIMARY,
          'margin': '0'
        });
      } else {
        setStyles(el, {
          'font-family': FONT,
          'font-weight': '400',
          'font-size': '16px',
          'line-height': '24px',
          'color': TEXT_SECONDARY,
          'margin': '0'
        });
      }
    });

    /* Primary-Button ("Bewertung schreiben") */
    widget.querySelectorAll('.jm-button.jm-button--primary').forEach(stylePrimaryButton);

    /* Secondary-Buttons (Filter) — rund, Icon-only-Look */
    widget.querySelectorAll('.jm-button.jm-button--secondary').forEach(styleIconButton);

    /* Sort-Filter (native <select> in einem Wrapper mit Icons) */
    widget.querySelectorAll('.jm-sort-filter').forEach(styleSortFilter);

    /* Review-Karten */
    widget.querySelectorAll('.jdgm-review-card').forEach(styleReviewCard);
  }

  function stylePrimaryButton(btn) {
    if (btn.dataset.koebiBtnStyled === '1') return;
    btn.dataset.koebiBtnStyled = '1';

    setStyles(btn, {
      'display': 'inline-flex',
      'align-items': 'center',
      'justify-content': 'center',
      'gap': '4px',
      'min-height': '48px',
      'height': 'auto',
      'padding': '14px 20px',
      'border': '0',
      'border-radius': '16px',
      'background-color': TEXT_PRIMARY,
      'background': TEXT_PRIMARY,
      'color': TEXT_INVERSE,
      'font-family': FONT,
      'font-size': '16px',
      'font-weight': '400',
      'line-height': '20px',
      'letter-spacing': '0.25px',
      'text-decoration': 'none',
      'text-transform': 'none',
      'white-space': 'nowrap',
      'cursor': 'pointer',
      'box-shadow': 'none',
      'transition': 'background-color 0.15s ease, color 0.15s ease'
    });

    btn.addEventListener('mouseenter', function () {
      setStyles(btn, {
        'background-color': BG_BUTTON_HOVER,
        'background': BG_BUTTON_HOVER,
        'color': TEXT_PRIMARY
      });
    });
    btn.addEventListener('mouseleave', function () {
      setStyles(btn, {
        'background-color': TEXT_PRIMARY,
        'background': TEXT_PRIMARY,
        'color': TEXT_INVERSE
      });
    });
  }

  function styleIconButton(btn) {
    if (btn.dataset.koebiIconBtnStyled === '1') return;
    btn.dataset.koebiIconBtnStyled = '1';

    setStyles(btn, {
      'display': 'inline-flex',
      'align-items': 'center',
      'justify-content': 'center',
      'width': '40px',
      'height': '40px',
      'min-width': '40px',
      'padding': '0',
      'border': '1px solid ' + BORDER,
      'border-radius': '9999px',
      'background': 'transparent',
      'background-color': 'transparent',
      'color': TEXT_PRIMARY,
      'cursor': 'pointer',
      'box-shadow': 'none',
      'transition': 'background-color 0.15s ease, border-color 0.15s ease'
    });

    /* Text-Span (z.B. "Filter") ausblenden — wir wollen nur das Icon */
    btn.querySelectorAll('.jm-text, .jm-custom-answer-filter__text').forEach(function (el) {
      setStyles(el, { 'display': 'none' });
    });

    /* Icon-Wrapper auf Auto-Layout zwingen */
    btn.querySelectorAll('.jm-with-icon, .jm-custom-answer-filter__icon').forEach(function (el) {
      setStyles(el, {
        'display': 'inline-flex',
        'align-items': 'center',
        'justify-content': 'center',
        'gap': '0',
        'width': 'auto',
        'height': 'auto'
      });
    });

    btn.querySelectorAll('svg').forEach(function (svg) {
      setStyles(svg, { 'width': '18px', 'height': '18px', 'color': 'inherit' });
    });

    btn.addEventListener('mouseenter', function () {
      setStyles(btn, {
        'background-color': BG_BUTTON_HOVER,
        'background': BG_BUTTON_HOVER,
        'border-color': TEXT_PRIMARY
      });
    });
    btn.addEventListener('mouseleave', function () {
      setStyles(btn, {
        'background-color': 'transparent',
        'background': 'transparent',
        'border-color': BORDER
      });
    });
  }

  function styleSortFilter(wrap) {
    if (wrap.dataset.koebiSortStyled === '1') return;
    wrap.dataset.koebiSortStyled = '1';

    /* Wrapper als runder Icon-Button — Native-<select> liegt transparent
       darueber damit der Click-Trigger erhalten bleibt. */
    setStyles(wrap, {
      'position': 'relative',
      'display': 'inline-flex',
      'align-items': 'center',
      'justify-content': 'center',
      'width': '40px',
      'height': '40px',
      'min-width': '40px',
      'padding': '0',
      'border': '1px solid ' + BORDER,
      'border-radius': '9999px',
      'background': 'transparent',
      'color': TEXT_PRIMARY,
      'cursor': 'pointer',
      'box-sizing': 'border-box',
      'transition': 'background-color 0.15s ease, border-color 0.15s ease'
    });

    /* Sort-Icon zentriert */
    var sortIcon = wrap.querySelector('.jm-sort-filter__icon--sort');
    if (sortIcon) {
      setStyles(sortIcon, {
        'display': 'inline-flex',
        'align-items': 'center',
        'justify-content': 'center',
        'width': '18px',
        'height': '18px',
        'color': TEXT_PRIMARY,
        'pointer-events': 'none'
      });
      sortIcon.querySelectorAll('svg').forEach(function (svg) {
        setStyles(svg, { 'width': '18px', 'height': '18px' });
      });
    }
    /* Chevron-Icon ausblenden — bei rundem Icon-Button stoerend */
    var chevron = wrap.querySelector('.jm-sort-filter__icon--chevron');
    if (chevron) setStyles(chevron, { 'display': 'none' });

    /* Native <select> transparent ueber den Button legen, Click-Target */
    var sel = wrap.querySelector('select');
    if (sel) {
      setStyles(sel, {
        'position': 'absolute',
        'inset': '0',
        'width': '100%',
        'height': '100%',
        'opacity': '0',
        'cursor': 'pointer',
        'appearance': 'none',
        '-webkit-appearance': 'none',
        'border': '0',
        'background': 'transparent',
        'padding': '0',
        'margin': '0'
      });
    }

    wrap.addEventListener('mouseenter', function () {
      setStyles(wrap, { 'background-color': BG_BUTTON_HOVER, 'border-color': TEXT_PRIMARY });
    });
    wrap.addEventListener('mouseleave', function () {
      setStyles(wrap, { 'background-color': 'transparent', 'border-color': BORDER });
    });
  }

  function styleReviewCard(card) {
    setStyles(card, {
      'display': 'block',
      'border': '1px solid ' + BORDER,
      'border-radius': '12px',
      'padding': '20px',
      'margin': '16px 0 0',
      'background': BG_CARD,
      'background-color': BG_CARD,
      'color': TEXT_PRIMARY,
      'box-sizing': 'border-box',
      'font-family': FONT
    });

    /* Inneres .jdgm-review-card__content hat eigenes Padding via
       --jm-box-padding (var(--jm-space-600)) — neutralisieren. */
    card.querySelectorAll('.jdgm-review-card__content').forEach(function (el) {
      setStyles(el, { 'padding': '0', '--jm-box-padding': '0' });
    });

    /* Tim 2026-05-24: Innere .jm-box-Wrapper haben einen impliziten
       Hintergrund (Default-Card-Background von JM) — sichtbar als
       leicht hellere Flaeche hinter dem Review-Text. Alle inneren
       Box-Container und Body-Wrapper transparent. */
    card.querySelectorAll('.jm-box, .jdgm-reply-item__content, .jdgm-review-card__body').forEach(function (el) {
      setStyles(el, {
        'background': 'transparent',
        'background-color': 'transparent',
        'box-shadow': 'none'
      });
    });

    card.querySelectorAll('.jdgm-review-card__name').forEach(function (el) {
      setStyles(el, {
        'font-family': FONT,
        'font-weight': '700',
        'font-size': '16px',
        'line-height': '24px',
        'color': TEXT_PRIMARY,
        'margin': '0'
      });
    });

    /* Title: Tim 2026-05-24 — Groesse aus dem Widget (jm-heading-sm)
       beibehalten, NUR Schriftart + Farbe + Margin angleichen. */
    card.querySelectorAll('.jdgm-review-card__title').forEach(function (el) {
      setStyles(el, {
        'font-family': FONT,
        'color': TEXT_PRIMARY,
        'margin': '8px 0 4px'
      });
    });

    card.querySelectorAll('.jdgm-review-card__body').forEach(function (el) {
      setStyles(el, {
        'font-family': FONT,
        'font-weight': '400',
        'font-size': '16px',
        'line-height': '24px',
        'color': TEXT_PRIMARY,
        'margin': '0'
      });
    });
    card.querySelectorAll('.jdgm-review-card__body p').forEach(function (el) {
      setStyles(el, {
        'font-family': FONT,
        'font-weight': '400',
        'font-size': '16px',
        'line-height': '24px',
        'color': TEXT_PRIMARY,
        'background': 'transparent',
        'background-color': 'transparent',
        'margin': '0'
      });
    });
  }

  /* ---------- Observer & Init ---------- */

  function watchWidget(widget) {
    if (widget.dataset.koebiWidgetWatched === '1') return;
    widget.dataset.koebiWidgetWatched = '1';

    styleWidget(widget);

    /* Debounce: Vue rendert beim Filter/Sort potenziell viele Mutations
       in einem Frame — wir reapply'en max. einmal pro 100ms. */
    var pending = null;
    new MutationObserver(function () {
      if (pending) return;
      pending = setTimeout(function () {
        pending = null;
        styleWidget(widget);
      }, 100);
    }).observe(widget, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class', 'aria-label']
    });
  }

  function tryInitWidget() {
    var widgets = document.querySelectorAll(
      '.jdgm-review-widget.jdgm-widget-revamp, .jdgm-widget.jdgm-review-widget'
    );
    if (!widgets.length) {
      setTimeout(tryInitWidget, 300);
      return;
    }
    widgets.forEach(watchWidget);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tryInitWidget);
  } else {
    tryInitWidget();
  }
})();
